import { createFileRoute } from "@tanstack/react-router";
import perfNow from "common/utils/perfNow";
import { EnvPreset } from "gfx/EnvPreset";
import { estimateHeightMapGPUMemory, forgetCachedHeightmaps } from "gfx/materials/baking/heightMapFromDesignMaterial";
import { TEXTURE_ATLAS_TEXTURE_WIDTH } from "gfx/materials/textureAtlasConstants";
import { usePreloadedMaterialTextures } from "gfx/materials/usePreloadedMaterialTextures";
import { formatBytes } from "gfx/MeshDataStats";
import PerspectiveControls from "gfx/PerspectiveControls";
import { envPresetSceneLights } from "gfx/sceneLights";
import { MaterialSharedParameterGroup } from "gfx/threejs/higharc/MaterialSharedParameterGroup";
import THREEViewer from "gfx/THREEViewer";
import getPageTitle from "lib/utils/getPageTitle";
import { useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import type { StressTestTextures } from "test/util/materialStressTestUtil";
import { materialStressTests, renderTestMaterials, MaterialStressTestType } from "test/util/materialStressTestUtil";
import type * as THREE from "three";
import Checkbox from "ui/forms/CheckboxOld";
import Select from "ui/forms/Select";
import withAuthentication from "../../../components/withAuthentication";
import styles from "../styles/gfxDebugPage.module.scss";

const defaultCameraSettings = {
  name: "Default",
  fov: 20,
  ortho: false,
  x: 0,
  y: 0,
  z: 2000,
  targetX: 0,
  targetY: 0,
  targetZ: 0,
  upX: 0,
  upY: 1,
  upZ: 0,
  cameraNear: 5,
  cameraFar: 5000,
};

function MaterialStressTestPage() {
  const testTexturesRef = useRef<StressTestTextures | undefined>(undefined);

  const materialList = useRef<THREE.Material[]>([]);
  const manuallyAccountedMemory = useRef<number>(0);

  const [testType, setTestType] = useState<MaterialStressTestType>(MaterialStressTestType.PARALLAX_MAP_GENERATION);
  const [refreshAllOnEveryIteration, setRefreshAllOnEveryIteration] = useState<boolean>(true);
  const [parallaxEffectEnabled, setParallaxEffectEnabled] = useState<boolean>(true);

  const [materialCount, setMaterialCount] = useState<number>(0);
  const [gpuMemoryCounter, setGpuMemoryCounter] = useState<number>(0);
  const [lastPerformanceResult, setLastPerformanceResult] = useState<number>(0);

  const [testRunning, setTestRunning] = useState<NodeJS.Timeout | undefined>(undefined);
  const [autoStop, setAutoStop] = useState<boolean>(false);

  const viewerContainerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<THREEViewer | undefined>(undefined);

  const manualAllocations = useRef<THREE.Texture[]>([]);

  const { fetchedTextures } = usePreloadedMaterialTextures();
  const displayMeshesRef = useRef<THREE.Mesh[] | undefined>(undefined);

  function startTesting() {
    MaterialSharedParameterGroup.instance.sharedDefines.parallaxEffectEnabled = parallaxEffectEnabled;

    const viewer = viewerRef.current;
    const textures = fetchedTextures.current;
    if (!viewer || !textures) {
      return;
    }
    const renderer = viewer.viewerRenderer.renderer;

    let iterationIndex = 0;

    const interval = setInterval(async () => {
      const t0 = perfNow();

      const test = materialStressTests[testType];

      const refreshAll = refreshAllOnEveryIteration || test.alwaysRefreshAllMaterials;
      if (refreshAll) {
        materialList.current = [];
        manuallyAccountedMemory.current = 0;
        await test.resetCache(renderer);
      }

      const generatedCount = refreshAll ? (iterationIndex + 1) * test.increment : test.increment;
      const startParamsIndex = refreshAll ? 0 : iterationIndex * test.increment;

      const { materials: newMaterials, manuallyAccountedMemoryBytes: memoryBytes } = await test.generateMaterials(
        startParamsIndex,
        generatedCount,
        { manualAllocations, testTexturesRef, viewer, textures },
      );

      materialList.current.push(...newMaterials);
      manuallyAccountedMemory.current += memoryBytes;

      removeMeshes();
      displayMeshesRef.current = await renderTestMaterials(viewer.scene, materialList.current);

      viewer.viewerRenderer?.forceRenderAndFinish();
      const t1 = perfNow();
      const measurement = t1 - t0;
      console.log("Last performance test result: " + measurement + " milliseconds");
      setLastPerformanceResult(measurement);
      setMaterialCount(materialList.current.length);

      const textureAtlasMemory =
        (testTexturesRef.current?.length() ?? 0) * TEXTURE_ATLAS_TEXTURE_WIDTH * TEXTURE_ATLAS_TEXTURE_WIDTH;
      setGpuMemoryCounter(estimateHeightMapGPUMemory(renderer) + manuallyAccountedMemory.current + textureAtlasMemory);

      ++iterationIndex;
      if (iterationIndex >= 50 && autoStop) {
        clearInterval(interval);
        setTestRunning(undefined);
      }
    }, 1000);

    setTestRunning(interval);
  }

  useEffect(() => {
    // Note that not loading the env map will result in the placeholder env map being used.
    const sceneLights = {
      ...envPresetSceneLights[EnvPreset.WHITEBOX],
      interiorAmbientLight: undefined, // disable the interior ambient light
    };

    const viewer = new THREEViewer({
      container: viewerContainerRef.current,
      options: {
        gui: false,
        ssfxRenderer: true,
        animate: true,
        sceneLights,
        debugFeaturesEnabled: true,
        cameraSettings: defaultCameraSettings,
      },
    });

    void (async () => {
      viewerRef.current = viewer;
    })();

    return () => {
      if (viewerRef.current) {
        const viewer = viewerRef.current;
        viewer.scene.clear();
        viewer.dispose();
        viewerRef.current = undefined;
      }
    };
  }, []);

  function removeMeshes() {
    const displayMeshes = displayMeshesRef.current;
    const viewer = viewerRef.current;
    if (displayMeshes === undefined || !viewer) {
      return;
    }
    for (let meshIndex = 0; meshIndex < displayMeshes.length; ++meshIndex) {
      const mesh = displayMeshes[meshIndex];
      viewer.scene.remove(mesh);
    }
    displayMeshesRef.current = [];
  }

  return (
    <>
      <Head>
        <title>{getPageTitle("Material Load Stress Test - Incremental")}</title>
        <meta name="robots" content="noindex" />
      </Head>
      <div>
        <div className={styles.fullScreenViewerContainer} ref={viewerContainerRef} />
        <div className={styles.buttonContainerBottomLeft}>
          <div>
            Number of materials: {materialCount}. Estimated GPU memory in use for textures:{" "}
            {formatBytes(gpuMemoryCounter)} Number of textures: {testTexturesRef.current?.length() ?? 0}
          </div>
          <button
            onClick={() => {
              const viewer = viewerRef.current;
              if (!viewer) {
                return;
              }
              viewer.setCameraSettings(defaultCameraSettings);
              const cameraControls = new PerspectiveControls(viewer.camera, viewer.container, 0, 0, 0);
              viewer.setControls(cameraControls);
            }}
          >
            Reset Camera
          </button>
          <button
            onClick={() => {
              if (!viewerRef.current || !fetchedTextures.current) {
                return;
              }
              if (testRunning !== undefined) {
                clearInterval(testRunning);
                setTestRunning(undefined);
              } else {
                startTesting();
              }
            }}
          >
            Start/Stop Testing
          </button>
          <button
            onClick={() => {
              const renderer = viewerRef.current?.viewerRenderer.renderer;
              if (renderer) {
                removeMeshes();
                void forgetCachedHeightmaps(renderer);
                materialList.current = [];
                manuallyAccountedMemory.current = 0;
                for (const texture of manualAllocations.current) {
                  texture.dispose();
                }
                manualAllocations.current = [];
                setMaterialCount(0);
                setGpuMemoryCounter(0);
              }
            }}
          >
            Clear
          </button>
          <Select
            value={MaterialStressTestType[testType]}
            options={[
              {
                label: "Parallax map generation",
                value: MaterialStressTestType[MaterialStressTestType.PARALLAX_MAP_GENERATION],
              },
              { label: "Texture allocation", value: MaterialStressTestType[MaterialStressTestType.TEXTURE_ALLOCATION] },
              {
                label: "Shader variants",
                value: MaterialStressTestType[MaterialStressTestType.MATERIAL_SHADER_VARIANTS],
              },
              { label: "Texture atlas loading", value: MaterialStressTestType[MaterialStressTestType.TEXTURE_ATLAS] },
            ]}
            onChange={(val) => setTestType(MaterialStressTestType[val as string])}
            disabled={testRunning !== undefined}
          />
          <Checkbox
            label="Automatically stop after 50 iterations"
            checked={autoStop}
            onChange={(e) => setAutoStop(e.target.checked)}
            disabled={testRunning !== undefined}
          />
          <Checkbox
            label="Refresh textures/shaders on every iteration"
            checked={refreshAllOnEveryIteration}
            onChange={(e) => setRefreshAllOnEveryIteration(e.target.checked)}
            disabled={testRunning !== undefined}
          />
          <Checkbox
            label="Parallax effect"
            checked={parallaxEffectEnabled}
            onChange={(e) => setParallaxEffectEnabled(e.target.checked)}
            disabled={testRunning !== undefined}
          />
          <div>Latest load time measurement: {lastPerformanceResult.toFixed(2)}</div>
        </div>
      </div>
    </>
  );
}

export const Route = createFileRoute("/debug/gfx-stress/material-stress-incremental")({
  component: withAuthentication(MaterialStressTestPage, { superAdmin: true }),
});
